import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Eye, EyeOff, GripVertical, RotateCcw, SlidersHorizontal } from "lucide-react";
import {
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";

import OverlayShell from "../components/OverlayShell";
import { NAV_BY_KEY, type NavKey } from "../components/nav";
import { errorMessageOf, useAuth } from "../lib/auth";
import {
  fetchNavPrefs,
  NAV_ICON_CHOICES,
  resetNavPrefs,
  resolveNavItems,
  saveNavPrefs,
  type NavPrefs,
  type ResolvedNavItem,
} from "../lib/navPrefs";
import { queryKeys } from "../lib/queryKeys";
import { useToast } from "../lib/toast";

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** ドラッグ中の持ち上げた行(→ TagManager と同じ作り) */
interface DragState {
  index: number;
  dy: number;
  height: number;
}

function dropIndexOf(drag: DragState, count: number): number {
  return clamp(drag.index + Math.round(drag.dy / drag.height), 0, count - 1);
}

/** 現在の見た目(resolve 済みの並び)から、保存する形を組み立てる */
function prefsFrom(items: ResolvedNavItem[]): NavPrefs {
  return {
    order: items.map((item) => item.key),
    hidden: items.filter((item) => item.hidden).map((item) => item.key),
    labels: Object.fromEntries(
      items.filter((item) => item.label !== defaultLabelOf(item.key)).map((i) => [i.key, i.label]),
    ),
    icons: Object.fromEntries(
      items.filter((item) => item.iconName !== null).map((i) => [i.key, i.iconName ?? ""]),
    ),
  };
}

function defaultLabelOf(key: NavKey): string {
  return NAV_BY_KEY[key].label;
}

/**
 * メニューの編集。並び順(ドラッグ)・表示on/off・表示名・アイコンを
 * 自分の設定として保存する。相手のメニューには影響しない。
 */
export default function NavCustomizer({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { session } = useAuth();
  const userId = session?.user.id;

  const prefs = useQuery({ queryKey: queryKeys.navPrefs, queryFn: fetchNavPrefs });
  const items = resolveNavItems(prefs.data ?? null);

  const [renaming, setRenaming] = useState<{ key: NavKey; draft: string } | null>(null);
  const [iconPickerFor, setIconPickerFor] = useState<NavKey | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const dragStartY = useRef(0);

  const save = useMutation({
    mutationFn: async (next: NavPrefs) => {
      if (userId === undefined) return;
      await saveNavPrefs(userId, next);
    },
    onError: (e) => {
      toast(errorMessageOf(e), "error");
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.navPrefs });
    },
  });

  /** 先に画面へ反映してから保存する。メニューの見た目は即時に変わってほしい */
  function apply(next: ResolvedNavItem[]) {
    const nextPrefs = prefsFrom(next);
    queryClient.setQueryData(queryKeys.navPrefs, nextPrefs);
    save.mutate(nextPrefs);
  }

  function withItem(key: NavKey, patch: Partial<ResolvedNavItem>) {
    apply(items.map((item) => (item.key === key ? { ...item, ...patch } : item)));
  }

  function startDrag(event: ReactPointerEvent<HTMLButtonElement>, index: number) {
    const row = event.currentTarget.closest("li");
    if (row === null) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartY.current = event.clientY;
    setDrag({ index, dy: 0, height: row.offsetHeight });
  }

  function moveDrag(event: ReactPointerEvent<HTMLButtonElement>) {
    setDrag((current) =>
      current === null ? null : { ...current, dy: event.clientY - dragStartY.current },
    );
  }

  function endDrag() {
    if (drag === null) return;
    const to = dropIndexOf(drag, items.length);
    const from = drag.index;
    setDrag(null);
    if (to === from) return;
    const next = [...items];
    const [row] = next.splice(from, 1);
    if (row === undefined) return;
    next.splice(to, 0, row);
    apply(next);
  }

  const dropIndex = drag === null ? null : dropIndexOf(drag, items.length);

  function rowStyle(index: number): CSSProperties | undefined {
    if (drag === null || dropIndex === null) return undefined;
    if (index === drag.index) return { transform: `translateY(${String(drag.dy)}px)` };
    if (drag.index < index && index <= dropIndex) {
      return { transform: `translateY(-${String(drag.height)}px)`, transition: "transform 150ms" };
    }
    if (dropIndex <= index && index < drag.index) {
      return { transform: `translateY(${String(drag.height)}px)`, transition: "transform 150ms" };
    }
    return { transition: "transform 150ms" };
  }

  async function resetAll() {
    if (userId === undefined) return;
    try {
      await resetNavPrefs(userId);
      await queryClient.invalidateQueries({ queryKey: queryKeys.navPrefs });
      toast("メニューを初期状態に戻しました。");
    } catch (e) {
      toast(errorMessageOf(e), "error");
    }
  }

  return (
    <OverlayShell title="メニューを編集" icon={SlidersHorizontal} onClose={onClose}>
      <div className="flex w-full flex-col gap-3 p-4">
        <p className="text-xs text-[var(--color-sub)]">
          自分のメニューだけが変わります。持ち手のドラッグで並べ替え、目のマークで表示を切り替え。
          名前を押すと変えられ、アイコンを押すと選べます。設定タブは隠せません。
        </p>

        <ul className="flex flex-col overflow-hidden rounded-xl border border-[var(--color-line)] bg-white">
          {items.map((item, index) => {
            const Icon = item.icon;
            const lifted = drag !== null && drag.index === index;
            return (
              <li
                key={item.key}
                style={rowStyle(index)}
                className={`flex flex-col border-b border-[var(--color-line)] bg-white last:border-b-0 ${
                  lifted ? "relative z-10 shadow-md" : ""
                }`}
              >
                <div
                  className={`flex items-center gap-2 px-3 py-3 ${item.hidden ? "opacity-45" : ""}`}
                >
                  <button
                    type="button"
                    onPointerDown={(event) => {
                      startDrag(event, index);
                    }}
                    onPointerMove={moveDrag}
                    onPointerUp={endDrag}
                    onPointerCancel={() => {
                      setDrag(null);
                    }}
                    aria-label={`${item.label} をドラッグして並べ替え`}
                    className={`touch-none text-[var(--color-sub)] ${lifted ? "cursor-grabbing" : "cursor-grab"}`}
                  >
                    <GripVertical className="size-5" aria-hidden />
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setIconPickerFor((current) => (current === item.key ? null : item.key));
                    }}
                    aria-label={`${item.label} のアイコンを変える`}
                    className="flex size-8 items-center justify-center rounded-lg border border-[var(--color-line)]"
                  >
                    <Icon className="size-4" aria-hidden />
                  </button>

                  {renaming?.key === item.key ? (
                    <form
                      onSubmit={(event) => {
                        event.preventDefault();
                        const name = renaming.draft.trim();
                        withItem(item.key, {
                          label: name === "" ? defaultLabelOf(item.key) : name,
                        });
                        setRenaming(null);
                      }}
                      className="flex flex-1 items-center gap-1"
                    >
                      <input
                        autoFocus
                        value={renaming.draft}
                        maxLength={10}
                        onChange={(event) => {
                          setRenaming({ key: item.key, draft: event.target.value });
                        }}
                        className="w-full min-w-0 flex-1 rounded-lg border border-[var(--color-line)] px-2 py-1 text-base"
                      />
                      <button
                        type="submit"
                        aria-label="保存"
                        className="shrink-0 text-[var(--color-teal)]"
                      >
                        <Check className="size-5" aria-hidden />
                      </button>
                    </form>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setRenaming({ key: item.key, draft: item.label });
                      }}
                      className="flex-1 text-left text-sm"
                    >
                      {item.label}
                      {item.label !== defaultLabelOf(item.key) && (
                        <span className="ml-1 text-[10px] text-[var(--color-sub)]">
                          ({defaultLabelOf(item.key)})
                        </span>
                      )}
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => {
                      withItem(item.key, { hidden: !item.hidden });
                    }}
                    disabled={item.key === "settings"}
                    aria-pressed={!item.hidden}
                    aria-label={item.hidden ? `${item.label} を表示する` : `${item.label} を隠す`}
                    className="text-[var(--color-sub)] disabled:opacity-30"
                  >
                    {item.hidden ? (
                      <EyeOff className="size-5" aria-hidden />
                    ) : (
                      <Eye className="size-5" aria-hidden />
                    )}
                  </button>
                </div>

                {iconPickerFor === item.key && (
                  <div className="flex flex-wrap gap-1.5 border-t border-[var(--color-line)] bg-[var(--color-bg)] p-3">
                    {Object.entries(NAV_ICON_CHOICES).map(([name, Choice]) => (
                      <button
                        key={name}
                        type="button"
                        onClick={() => {
                          withItem(item.key, { iconName: name, icon: Choice });
                          setIconPickerFor(null);
                        }}
                        aria-label={`アイコン ${name}`}
                        className={`flex size-9 items-center justify-center rounded-lg border ${
                          item.iconName === name
                            ? "border-[var(--color-teal)] bg-white text-[var(--color-teal)]"
                            : "border-[var(--color-line)] bg-white text-[var(--color-sub)]"
                        }`}
                      >
                        <Choice className="size-4" aria-hidden />
                      </button>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <button
          type="button"
          onClick={() => void resetAll()}
          className="flex items-center justify-center gap-1 rounded-lg border border-[var(--color-line)] px-4 py-2 text-sm text-[var(--color-sub)]"
        >
          <RotateCcw className="size-4" aria-hidden />
          初期状態に戻す
        </button>
      </div>
    </OverlayShell>
  );
}
